/**
 * UsageLogsPanel — "Usage" sub-tab of Activity Logs.
 *
 * Answers "is the training team actually delivering?" by surfacing real
 * content CONSUMPTION (not logins/edits):
 *   • deck.presented   — trainer presented a module in fullscreen/projector
 *   • live.session_run — a live quiz session that was actually started
 *
 * Two views: a delivery feed + a summary strip, and an "inactive presenters"
 * roster (deliverers who've gone quiet in the window). Gating + cross-org
 * filter mirror the other Activity Logs tabs.
 */
import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Loader2, MonitorPlay, Radio, Users, Clock, UserX, Presentation,
} from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

const WINDOWS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
];

function fmtTime(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

function fmtDuration(secs) {
  if (secs === null || secs === undefined) return '—';
  const n = Math.max(0, Math.round(secs));
  if (n < 60) return `${n}s`;
  const m = Math.floor(n / 60);
  if (m < 60) return `${m}m ${n % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function StatCard({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="rounded-lg border bg-card p-4 flex items-start gap-3" data-testid={testId}>
      <span className="mt-0.5 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[var(--lumyn-content)]/10 text-[var(--lumyn-content)] shrink-0">
        <Icon className="w-4.5 h-4.5" />
      </span>
      <div className="min-w-0">
        <div className="text-2xl font-display font-bold tabular-nums text-foreground leading-tight">{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
        {sub && <div className="text-[11px] text-muted-foreground/80 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function TypePill({ type }) {
  const isDeck = type === 'deck.presented';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
      isDeck ? 'bg-[var(--lumyn-content)]/10 text-[var(--lumyn-content)]' : 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
    }`}>
      {isDeck ? <Presentation className="w-3 h-3" /> : <Radio className="w-3 h-3" />}
      {isDeck ? 'Deck' : 'Live quiz'}
    </span>
  );
}

export default function UsageLogsPanel({ orgs = [], isPlatformAdminPlus = false }) {
  const { get } = useApi();
  const [view, setView] = useState('feed');          // 'feed' | 'inactive'
  const [days, setDays] = useState('30');
  const [orgFilter, setOrgFilter] = useState('all');

  const [summary, setSummary] = useState(null);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [inactive, setInactive] = useState(null);
  const [loading, setLoading] = useState(true);

  const orgParam = (isPlatformAdminPlus && orgFilter !== 'all') ? `&org_id=${orgFilter}` : '';

  const loadSummary = useCallback(async () => {
    try {
      const res = await get(`/usage/summary?days=${days}${orgParam}`);
      setSummary(res);
    } catch { /* non-fatal */ }
  }, [get, days, orgParam]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (view === 'feed') {
        const res = await get(`/usage/events?days=${days}&limit=50&skip=${skip}${orgParam}`);
        setLogs(res.logs || []);
        setTotal(res.total || 0);
      } else {
        const res = await get(`/usage/inactive-presenters?days=${days}${orgParam}`);
        setInactive(res);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load usage');
    } finally {
      setLoading(false);
    }
  }, [get, view, days, skip, orgParam]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="usage-logs-panel">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border p-0.5 bg-muted/30">
          <button
            onClick={() => { setView('feed'); setSkip(0); }}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${view === 'feed' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="usage-view-feed"
          >Delivery feed</button>
          <button
            onClick={() => { setView('inactive'); setSkip(0); }}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${view === 'inactive' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="usage-view-inactive"
          >Inactive presenters</button>
        </div>
        <Select value={days} onValueChange={(v) => { setDays(v); setSkip(0); }}>
          <SelectTrigger className="w-40" data-testid="usage-window-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {WINDOWS.map(w => <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {isPlatformAdminPlus && (
          <Select value={orgFilter} onValueChange={(v) => { setOrgFilter(v); setSkip(0); }}>
            <SelectTrigger className="w-44" data-testid="usage-org-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All organizations</SelectItem>
              {orgs.map(o => <SelectItem key={o.org_id} value={o.org_id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={MonitorPlay} label="Decks presented" value={summary?.decks_presented ?? '—'} testId="usage-stat-decks" />
        <StatCard icon={Radio} label="Live quizzes run" value={summary?.live_sessions_run ?? '—'} testId="usage-stat-live" />
        <StatCard icon={Users} label="Active presenters" value={summary?.active_presenters ?? '—'} sub={`${summary?.participants_reached ?? 0} participants reached`} testId="usage-stat-presenters" />
        <StatCard icon={Clock} label="Total delivery time" value={fmtDuration(summary?.total_delivery_seconds)} testId="usage-stat-time" />
      </div>

      {/* Feed / Inactive */}
      <div className="rounded-lg border overflow-hidden">
        {view === 'feed' ? (
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr>
                <th className="text-left p-3 w-44">Time</th>
                <th className="text-left p-3">Type</th>
                <th className="text-left p-3">Presenter</th>
                <th className="text-left p-3">Content</th>
                <th className="text-left p-3 hidden lg:table-cell">Duration</th>
                <th className="text-left p-3">Reach</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="p-8 text-center text-muted-foreground"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center text-muted-foreground italic">No delivery activity in this window</td></tr>
              ) : logs.map((e) => (
                <tr key={e.usage_id} className="border-t hover:bg-muted/30" data-testid="usage-feed-row">
                  <td className="p-3 text-muted-foreground tabular-nums whitespace-nowrap">{fmtTime(e.started_at)}</td>
                  <td className="p-3"><TypePill type={e.event_type} /></td>
                  <td className="p-3">{e.user_name || e.user_email || e.user_id || '—'}</td>
                  <td className="p-3 max-w-[280px] truncate">{e.resource_title || '—'}</td>
                  <td className="p-3 hidden lg:table-cell tabular-nums">
                    {e.status === 'in_progress'
                      ? <span className="text-emerald-600 dark:text-emerald-400">In progress</span>
                      : fmtDuration(e.duration_seconds)}
                  </td>
                  <td className="p-3 tabular-nums">
                    {e.event_type === 'deck.presented'
                      ? <span>{e.reach ?? 0}{e.total ? `/${e.total}` : ''} <span className="text-muted-foreground text-xs">slides</span></span>
                      : <span>{e.reach ?? 0} <span className="text-muted-foreground text-xs">joined</span></span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr>
                <th className="text-left p-3">Presenter</th>
                <th className="text-left p-3">Role</th>
                <th className="text-left p-3 hidden lg:table-cell">Org</th>
                <th className="text-left p-3">Last delivery</th>
                <th className="text-left p-3">Quiet for</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="p-8 text-center text-muted-foreground"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></td></tr>
              ) : !inactive || inactive.presenters.length === 0 ? (
                <tr><td colSpan={5} className="p-8 text-center text-muted-foreground italic">
                  <UserX className="w-5 h-5 mx-auto mb-2 opacity-50" />
                  Everyone has delivered within the last {days} days 🎉
                </td></tr>
              ) : inactive.presenters.map((p) => (
                <tr key={p.user_id} className="border-t hover:bg-muted/30" data-testid="usage-inactive-row">
                  <td className="p-3">{p.name || p.email || p.user_id}</td>
                  <td className="p-3"><span className="px-2 py-0.5 rounded text-xs bg-muted text-muted-foreground">{p.role || '—'}</span></td>
                  <td className="p-3 hidden lg:table-cell">{p.org_name || '—'}</td>
                  <td className="p-3 text-muted-foreground">{p.last_active_at ? fmtTime(p.last_active_at) : <span className="italic">Never</span>}</td>
                  <td className="p-3">
                    {p.days_since_active === null || p.days_since_active === undefined
                      ? <span className="text-red-600 dark:text-red-400 font-medium">Never delivered</span>
                      : <span className="text-amber-600 dark:text-amber-400 tabular-nums">{p.days_since_active} days</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {view === 'feed' && total > 50 && (
          <div className="flex items-center justify-between p-3 border-t text-sm">
            <span className="text-muted-foreground">{skip + 1}–{Math.min(skip + 50, total)} of {total}</span>
            <div className="flex gap-2">
              <button className="px-3 py-1 rounded border text-sm disabled:opacity-40" disabled={skip === 0} onClick={() => setSkip(s => Math.max(0, s - 50))}>Previous</button>
              <button className="px-3 py-1 rounded border text-sm disabled:opacity-40" disabled={skip + 50 >= total} onClick={() => setSkip(s => s + 50)}>Next</button>
            </div>
          </div>
        )}
        {view === 'inactive' && inactive && (
          <div className="p-3 border-t text-xs text-muted-foreground">
            {inactive.presenters.length} of {inactive.total_considered} deliverers (trainers/editors) inactive in the last {days} days.
          </div>
        )}
      </div>
    </div>
  );
}
